import { Fragment } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';
import { hasCensus, hasZone } from '../features/zone/access.ts';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
  /** Items in the Zone group render under a small "Zone" label. */
  section?: 'zone';
};

// Google-Drive-style navigation drawer (desktop), ported from WapSign: full-width
// destination rows with a tonal pill on the active row, sitting under the top app
// bar on the chrome ground. Brand, user identity, theme and sign-out moved to the
// TopBar — the rail carries destinations only.
export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!user) return null;

  const isAdmin = user.isOrgAdmin || user.role === 'ADMIN';

  const census = hasCensus(user);
  const zone = hasZone(user);
  const items: SideItem[] = user.role === 'SYS_ADMIN'
    ? [
      { to: '/admin/orgs', label: dv.nav.orgs, icon: 'apartment', matchPrefix: '/admin' },
      { to: '/profile', label: dv.nav.profile, icon: 'person' },
    ]
    : [
      ...(census
        ? [
          { to: '/', label: dv.nav.dashboard, icon: 'dashboard' },
          { to: '/houses', label: dv.nav.houses, icon: 'home', matchPrefix: '/houses' },
          { to: '/family', label: 'Family tree', icon: 'family_history', matchPrefix: '/family' },
          { to: '/surveys', label: 'Surveys', icon: 'assignment', matchPrefix: '/surveys' },
          ...(isAdmin ? [{ to: '/dhaftharu', label: dv.nav.dhaftharu, icon: 'menu_book', matchPrefix: '/dhaftharu' }] : []),
        ]
        : []),
      ...(user.isOrgAdmin ? [{ to: '/settings', label: dv.nav.settings, icon: 'group', matchPrefix: '/settings' }] : []),
      ...(zone
        ? [
          { to: '/zone', label: 'Issues', icon: 'report', matchPrefix: '/zone/issues', section: 'zone' as const },
          { to: '/zone/dashboard', label: 'Zone dashboard', icon: 'insights', section: 'zone' as const },
          ...(user.isOrgAdmin ? [{ to: '/zone/settings', label: 'Zone settings', icon: 'tune', section: 'zone' as const }] : []),
        ]
        : []),
      { to: '/profile', label: dv.nav.profile, icon: 'person' },
    ];

  // The Issues row is also active on /zone/new and /zone/issues/$id; it is exact on /zone itself.
  const isActive = (item: SideItem): boolean => {
    if (item.to === '/zone') return pathname === '/zone' || pathname === '/zone/new' || pathname.startsWith('/zone/issues');
    return item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;
  };

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label="Primary"
      className="fixed bottom-0 left-0 top-16 z-30 hidden w-64 flex-col gap-1 overflow-y-auto bg-[hsl(var(--chrome))] px-3 py-4 md:flex"
    >
      <nav className="flex flex-col gap-0.5">
        {items.map((item, idx) => {
          const active = isActive(item);
          const startsZone = item.section === 'zone' && items[idx - 1]?.section !== 'zone';
          return (
            <Fragment key={item.to}>
            {startsZone && <p className="text-xs font-medium text-muted-foreground px-4 pt-4">Zone</p>}
            <Link
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] font-medium text-[hsl(var(--on-secondary-container))]'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
            </Fragment>
          );
        })}
      </nav>

      {/* Ecosystem jump */}
      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        <a href="https://office.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <span className="grid size-[22px] shrink-0 place-items-center">
            <svg width="20" height="20" aria-hidden="true" viewBox="0 0 48 48">
              <rect width="48" height="48" rx="13.5" fill="#006A60" />
              <path d="M9 15.5 17.5 33 24 21.5" fill="none" stroke="#9EF2E4" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M24 21.5 30.5 33 39 15.5" fill="none" stroke="#fff" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex-1 truncate">Wapplify Office</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
      </div>
    </aside>
  );
}
